import React from "react";
import { Instagram, Facebook } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";

const socials = [
  {
    name: "Instagram",
    href: "https://www.instagram.com/karnival.ent?igsh=MXYwd2IyczJiM2dheQ%3D%3D&utm_source=qr",
    icon: Instagram,
    testid: "footer-social-instagram",
  },
  {
    name: "Facebook",
    href: "#",
    icon: Facebook,
    testid: "footer-social-facebook",
  },
  {
    name: "TikTok",
    href: "#",
    // inline SVG for TikTok since lucide doesn't have it
    icon: function TikTokIcon(props) {
      return (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          {...props}
        >
          <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
        </svg>
      );
    },
    testid: "footer-social-tiktok",
  },
];

const navCols = [
  {
    title: "Explore",
    links: [
      { label: "About", href: "#about" },
      { label: "Services", href: "#services" },
      { label: "Gallery", href: "/gallery" },
      { label: "Testimonials", href: "#testimonials" },
      { label: "Contact", href: "/contact" },
    ],
  },
  {
    title: "Services",
    links: [
      { label: "DJ Entertainment", href: "#services" },
      { label: "Audio-Visual", href: "#services" },
      { label: "Event Décor", href: "#services" },
      { label: "Special Effects", href: "#services" },
      { label: "Full Production", href: "#services" },
    ],
  },
];

export default function Footer() {
  const navigate = useNavigate();
  const location = useLocation();

  const scrollTo = (e, href) => {
    e.preventDefault();
    if (href.startsWith("/")) {
      navigate(href);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    if (location.pathname !== "/") {
      navigate("/" + href);
      setTimeout(() => {
        const el = document.querySelector(href);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
      return;
    }
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <footer
      data-testid="site-footer"
      className="relative bg-[#050505] border-t border-white/10 pt-20 pb-10"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-12 gap-10 md:gap-12 pb-14 border-b border-white/10">
          <div className="md:col-span-5">
            <img
              src="/karnival-logo-transparent.png"
              alt="Karnival Events & Entertainment"
              className="h-36 md:h-44 w-auto object-contain mb-6 -ml-3"
              loading="lazy"
            />
            <p className="text-white/60 text-base leading-relaxed max-w-sm">
              Ottawa&rsquo;s premier event production company — world-class DJs,
              stunning décor, and jaw-dropping special effects for every
              celebration.
            </p>

            <div className="mt-8 flex items-center gap-3">
              {socials.map((s) => {
                const Icon = s.icon;
                return (
                  <a
                    key={s.name}
                    href={s.href}
                    target={s.href.startsWith("http") ? "_blank" : undefined}
                    rel="noopener noreferrer"
                    data-testid={s.testid}
                    aria-label={s.name}
                    className="h-11 w-11 rounded-full flex items-center justify-center border border-white/10 text-white/70 hover:text-black hover:bg-[#FFFFFF] hover:border-[#FFFFFF] transition-all duration-300"
                  >
                    <Icon className="h-4 w-4" strokeWidth={1.5} />
                  </a>
                );
              })}
            </div>
          </div>

          {navCols.map((col) => (
            <div key={col.title} className="md:col-span-3">
              <div className="text-[11px] uppercase tracking-[0.28em] text-[#FFFFFF] mb-5">
                {col.title}
              </div>
              <ul className="space-y-3">
                {col.links.map((l) => (
                  <li key={l.label}>
                    <a
                      href={l.href}
                      onClick={(e) => scrollTo(e, l.href)}
                      data-testid={`footer-link-${l.label
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, "-")}`}
                      className="text-white/60 hover:text-[#FFFFFF] transition-colors text-sm"
                    >
                      {l.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div className="md:col-span-1 hidden md:block" />
        </div>

        <div className="pt-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-xs text-white/40">
          <div data-testid="footer-copyright">
            © 2025 Karnival Events &amp; Entertainment — Ottawa, Ontario. All
            rights reserved.
          </div>
          <div className="flex items-center gap-6">
            <span>613-799-0786</span>
            <span>ent.karnival@gmail.com</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
